import { useState } from "react";
import { Mail, Phone, MapPin, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useLanguage } from "@/contexts/LanguageContext";
import { useContactMessage, usePublicContactContent } from "@/hooks/api/use-public";
import { Skeleton } from "@/components/ui/skeleton";

const Contact = () => {
  const { t, language } = useLanguage();
  const { mutate: sendMessage, isPending } = useContactMessage();
  const { data: contactContent, isLoading } = usePublicContactContent();

  const heroTitle = contactContent?.data?.hero_title
    ? (typeof contactContent.data.hero_title === 'string'
        ? contactContent.data.hero_title
        : contactContent.data.hero_title[language] || contactContent.data.hero_title.en)
    : t('contact.title');

  const heroSubtitle = contactContent?.data?.hero_subtitle
    ? (typeof contactContent.data.hero_subtitle === 'string'
        ? contactContent.data.hero_subtitle
        : contactContent.data.hero_subtitle[language] || contactContent.data.hero_subtitle.en)
    : t('contact.subtitle');

  const formTitle = contactContent?.data?.form_title
    ? (typeof contactContent.data.form_title === 'string'
        ? contactContent.data.form_title
        : contactContent.data.form_title[language] || contactContent.data.form_title.en)
    : t('contact.sendMessage');

  const formNameLabel = contactContent?.data?.form_name_label
    ? (typeof contactContent.data.form_name_label === 'string'
        ? contactContent.data.form_name_label
        : contactContent.data.form_name_label[language] || contactContent.data.form_name_label.en)
    : t('contact.fullName');

  const formEmailLabel = contactContent?.data?.form_email_label
    ? (typeof contactContent.data.form_email_label === 'string'
        ? contactContent.data.form_email_label
        : contactContent.data.form_email_label[language] || contactContent.data.form_email_label.en)
    : t('contact.email');

  const formPhoneLabel = contactContent?.data?.form_phone_label
    ? (typeof contactContent.data.form_phone_label === 'string'
        ? contactContent.data.form_phone_label
        : contactContent.data.form_phone_label[language] || contactContent.data.form_phone_label.en)
    : t('auth.phone');

  const formSubjectLabel = contactContent?.data?.form_subject_label
    ? (typeof contactContent.data.form_subject_label === 'string'
        ? contactContent.data.form_subject_label
        : contactContent.data.form_subject_label[language] || contactContent.data.form_subject_label.en)
    : t('contact.subject');

  const formMessageLabel = contactContent?.data?.form_message_label
    ? (typeof contactContent.data.form_message_label === 'string'
        ? contactContent.data.form_message_label
        : contactContent.data.form_message_label[language] || contactContent.data.form_message_label.en)
    : t('contact.message');

  const formSubmitButtonText = contactContent?.data?.form_submit_button_text
    ? (typeof contactContent.data.form_submit_button_text === 'string'
        ? contactContent.data.form_submit_button_text
        : contactContent.data.form_submit_button_text[language] || contactContent.data.form_submit_button_text.en)
    : t('contact.send');

  const phone = contactContent?.data?.phone || null;
  const email = contactContent?.data?.email || null;
  const address = contactContent?.data?.address
    ? (typeof contactContent.data.address === 'string'
        ? contactContent.data.address
        : contactContent.data.address[language] || contactContent.data.address.en)
    : null;

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage({
      name: formData.name,
      email: formData.email,
      phone: formData.phone || undefined,
      subject: formData.subject,
      message: formData.message,
    }, {
      onSuccess: () => {
        setFormData({ name: "", email: "", phone: "", subject: "", message: "" });
      }
    });
  };

  return (
    <div className="py-16">
      {/* Hero */}
      <section className="py-20 bg-muted">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            {isLoading ? (
              <>
                <Skeleton className="h-12 w-full max-w-2xl mx-auto mb-6" />
                <Skeleton className="h-6 w-full max-w-xl mx-auto" />
              </>
            ) : (
              <>
                <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6 animate-fade-up">
                  {heroTitle}
                </h1>
                <p className="text-lg text-muted-foreground animate-fade-up stagger-1">
                  {heroSubtitle}
                </p>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="text-2xl font-bold text-foreground mb-8">
                {t('contact.info')}
              </h2>
              <div className="space-y-6">
                {/* Phone */}
                <div className="flex items-start gap-4 p-6 bg-card rounded-xl shadow-md">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center text-primary">
                    <Phone size={24} />
                  </div>
                  <div>
                    <h3 className="font-bold text-foreground mb-1">{t('contact.phone')}</h3>
                    {isLoading || !phone ? (
                      <Skeleton className="h-5 w-32" />
                    ) : (
                      <p className="text-muted-foreground" dir="ltr">{phone}</p>
                    )}
                  </div>
                </div>
                {/* Email */}
                <div className="flex items-start gap-4 p-6 bg-card rounded-xl shadow-md">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center text-primary">
                    <Mail size={24} />
                  </div>
                  <div>
                    <h3 className="font-bold text-foreground mb-1">{t('contact.email')}</h3>
                    {isLoading || !email ? (
                      <Skeleton className="h-5 w-40" />
                    ) : (
                      <p className="text-muted-foreground">{email}</p>
                    )}
                  </div>
                </div>
                {/* Address */}
                <div className="flex items-start gap-4 p-6 bg-card rounded-xl shadow-md">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center text-primary">
                    <MapPin size={24} />
                  </div>
                  <div>
                    <h3 className="font-bold text-foreground mb-1">{t('contact.address')}</h3>
                    {isLoading || !address ? (
                      <Skeleton className="h-5 w-48" />
                    ) : (
                      <p className="text-muted-foreground">{address}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-card p-8 rounded-2xl shadow-lg">
              {isLoading ? (
                <>
                  <Skeleton className="h-8 w-48 mb-6" />
                  <Skeleton className="h-96" />
                </>
              ) : (
                <>
                  <h2 className="text-2xl font-bold text-foreground mb-6">
                    {formTitle}
                  </h2>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-4">
                      <Input
                        placeholder={formNameLabel}
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                      />
                      <Input
                        type="email"
                        placeholder={formEmailLabel}
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                      />
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <Input
                        placeholder={formPhoneLabel}
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      />
                      <Input
                        placeholder={formSubjectLabel}
                        value={formData.subject}
                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                        required
                      />
                    </div>
                    <Textarea
                      placeholder={formMessageLabel}
                      rows={5}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      required
                    />
                    <Button type="submit" size="lg" className="w-full" disabled={isPending}>
                      {isPending ? t('common.loading') || 'جاري الإرسال...' : formSubmitButtonText}
                      <Send size={18} />
                    </Button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
